import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useSettings } from '@/hooks/useSettings';
import { useFavorites } from '@/hooks/useFavorites';
import { useHistory } from '@/hooks/useHistory';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/contexts/AuthContext';
import type { Settings, Theme, TextSize, AvatarId, Favorite, HistoryEntry, Song, Lyrics } from '@/types';
import type { ToastType } from '@/hooks/useToast';

interface AppContextType {
  // Settings
  settings: Settings;
  updateTheme: (theme: Theme) => void;
  updateTextSize: (size: TextSize) => void;
  updateAvatar: (avatar: AvatarId) => void;

  // Favorites
  favorites: Favorite[];
  favoritesLoading: boolean;
  addFavorite: (songId: number) => Promise<boolean>;
  removeFavorite: (songId: number) => Promise<boolean>;
  toggleFavorite: (songId: number) => Promise<boolean>;
  isFavorite: (songId: number) => boolean;

  // History
  history: HistoryEntry[];
  historyLoading: boolean;
  removeHistoryEntry: (songId: number) => Promise<boolean>;
  clearHistory: () => Promise<boolean>;
  reloadHistory: () => Promise<void>;

  // Toasts
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  toastSuccess: (message: string) => void;
  toastError: (message: string) => void;
  toastInfo: (message: string) => void;
  toastWarning: (message: string) => void;

  // PWA
  deferredPrompt: any;
  isPwaInstalled: boolean;
  installPwa: () => Promise<boolean>;

  // Songs (preloaded)
  songs: Song[];
  songsLoading: boolean;
  getSongById: (id: number) => Song | undefined;

  // Lyrics (preloaded)
  lyrics: Lyrics[];
  lyricsLoading: boolean;
  getLyricsById: (id: number) => string | null;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const { settings, updateTheme, updateTextSize, updateAvatar } = useSettings();

  // Get auth state for cloud sync
  const { user, isAuthenticated } = useAuth();

  const {
    favorites,
    loading: favoritesLoading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite
  } = useFavorites({ userId: user?.id, isAuthenticated });
  const {
    history,
    loading: historyLoading,
    removeEntry: removeHistoryEntry,
    clearHistory,
    reload: reloadHistory
  } = useHistory({ userId: user?.id, isAuthenticated });
  const {
    showToast,
    success: toastSuccess,
    error: toastError,
    info: toastInfo,
    warning: toastWarning,
    toasts,
    dismissToast
  } = useToast();

  // PWA State
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isPwaInstalled, setIsPwaInstalled] = useState(false);

  // Preloaded songs
  const [songs, setSongs] = useState<Song[]>([]);
  const [songsLoading, setSongsLoading] = useState(true);

  // Preload songs on app start
  useEffect(() => {
    const loadSongs = async () => {
      try {
        const response = await fetch('/data/jem-songs.json');
        const data = await response.json();
        setSongs(data);
      } catch (error) {
        console.error('Error preloading songs:', error);
      } finally {
        setSongsLoading(false);
      }
    };
    loadSongs();
  }, []);

  const getSongById = useCallback((id: number) => {
    return songs.find(s => s.numero === id);
  }, [songs]);

  // Preloaded lyrics (lazy - loaded on first access)
  const [lyrics, setLyrics] = useState<Lyrics[]>([]);
  const [lyricsLoading, setLyricsLoading] = useState(true);
  const [lyricsRequested, setLyricsRequested] = useState(false);

  // Load lyrics on demand (not at app start to reduce initial load by ~194KB)
  useEffect(() => {
    if (!lyricsRequested) return;
    const loadLyrics = async () => {
      try {
        const response = await fetch('/data/jem-lyrics.json');
        const data = await response.json();
        setLyrics(data);
      } catch (error) {
        console.error('Error loading lyrics:', error);
      } finally {
        setLyricsLoading(false);
      }
    };
    loadLyrics();
  }, [lyricsRequested]);

  const getLyricsById = useCallback((id: number): string | null => {
    if (!lyricsRequested) {
      setLyricsRequested(true);
    }
    const lyric = lyrics.find(l => l.numero === id);
    return lyric?.texte || null;
  }, [lyrics, lyricsRequested]);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsPwaInstalled(true);
    }

    // Capture beforeinstallprompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      console.log('PWA install prompt captured');
    };

    // Capture appinstalled
    const handleAppInstalled = () => {
      setIsPwaInstalled(true);
      setDeferredPrompt(null);
      toastSuccess('Application installée avec succès !');
      console.log('PWA installed');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [toastSuccess]);

  const installPwa = async () => {
    if (!deferredPrompt) return false;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    setDeferredPrompt(null);
    return outcome === 'accepted';
  };

  const value: AppContextType = {
    settings,
    updateTheme,
    updateTextSize,
    updateAvatar,
    favorites,
    favoritesLoading,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    isFavorite,
    history,
    historyLoading,
    removeHistoryEntry,
    clearHistory,
    reloadHistory,
    showToast,
    toastSuccess,
    toastError,
    toastInfo,
    toastWarning,
    deferredPrompt,
    isPwaInstalled,
    installPwa,
    songs,
    songsLoading,
    getSongById,
    lyrics,
    lyricsLoading,
    getLyricsById
  };

  return (
    <AppContext.Provider value={value}>
      {children}
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`
              toast-enter px-4 py-3 rounded-lg shadow-lg min-w-[300px] max-w-md cursor-pointer
              ${toast.type === 'success' ? 'bg-green-500 text-white' : ''}
              ${toast.type === 'error' ? 'bg-red-500 text-white' : ''}
              ${toast.type === 'info' ? 'bg-blue-500 text-white' : ''}
              ${toast.type === 'warning' ? 'bg-yellow-500 text-black' : ''}
            `}
            onClick={() => dismissToast(toast.id)}
          >
            <p className="text-sm font-medium">{toast.message}</p>
          </div>
        ))}
      </div>
    </AppContext.Provider>
  );
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
